import {
	SelectVendor,
	type SelectVendorProps,
} from "@au-schein-de/au-ui-library";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useDebouncedCallback } from "~/hooks/use-debounced-callback.hook";
import {
	getSelectVendorListHeight,
	isDesktopViewport,
} from "./responsive-select-vendor-height.util";
import { usePostalCodeFocused } from "./use-postal-code-focused.hook";

const SCROLL_TO_TOP_DELAY_MS = 150;

interface ResponsiveSelectVendorProps extends SelectVendorProps {
	mobileMapSlot?: ReactNode;
	onDropdownOpenChange?: (isOpen: boolean) => void;
}

/**
 * Wrapper around {SelectVendor} component that stretches it's height over the product list in mobile view
 */
export function ResponsiveSelectVendor({
	mobileMapSlot,
	onDropdownOpenChange,
	...props
}: ResponsiveSelectVendorProps) {
	const triggerRef = useRef<HTMLButtonElement>(null);
	const isDropdownOpenRef = useRef(false);
	const isPostalCodeFocused = usePostalCodeFocused();
	const mobileMapSlotRef = useRef(mobileMapSlot);
	mobileMapSlotRef.current = mobileMapSlot;
	const postalCodeFocusedRef = useRef(isPostalCodeFocused);
	postalCodeFocusedRef.current = isPostalCodeFocused;
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const [, setRenderKey] = useState<number>(0);

	useEffect(() => {
		void isPostalCodeFocused;
		setRenderKey((k) => k + 1);
	}, [isPostalCodeFocused]);

	const scrollToTop = useCallback(() => {
		const timer = setTimeout(() => {
			scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
		}, SCROLL_TO_TOP_DELAY_MS);
		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		if (!props.value || !mobileMapSlotRef.current) return;
		return scrollToTop();
	}, [props.value, scrollToTop]);

	const variantRef = useRef(props.variant);
	variantRef.current = props.variant;
	const footerSlotRef = useRef(props.footerSlot);
	footerSlotRef.current = props.footerSlot;
	const onDropdownOpenChangeRef = useRef(onDropdownOpenChange);
	onDropdownOpenChangeRef.current = onDropdownOpenChange;

	const WithHeight = useMemo(
		() =>
			({ children }: { children: ReactNode }) => {
				const innerHeight =
					typeof window === "undefined" ? 0 : window.innerHeight;
				const triggerBottom =
					triggerRef.current?.getBoundingClientRect().bottom ?? 0;
				const isDesktop = isDesktopViewport();
				const isPharmacy = variantRef.current === "pharmacy";
				const listHeight = getSelectVendorListHeight({
					viewportHeight: innerHeight,
					triggerBottom,
					searchSectionHeightPx: SelectVendor.SEARCH_SECTION_HEIGHT_PX,
					hasFooterSlot: Boolean(footerSlotRef.current),
					isPharmacy,
					isDropdownOpen: isDropdownOpenRef.current,
					isDesktop,
				});
				const scrollClasses = [
					"min-h-0",
					"overflow-y-auto md:overflow-y-hidden",
					"[&>[data-radix-scroll-area-viewport]]:!h-auto",
					"[&>[data-radix-scroll-area-viewport]]:!overflow-y-visible",
					"md:[&>[data-radix-scroll-area-viewport]]:!h-full",
					"md:[&>[data-radix-scroll-area-viewport]]:!overflow-y-scroll",
				].join(" ");
				return (
					<div
						ref={scrollContainerRef}
						className={scrollClasses}
						style={{ height: `${listHeight}px` }}
					>
						{mobileMapSlotRef.current && !postalCodeFocusedRef.current && (
							<>
								<div className="px-4 py-2 md:hidden">
									{mobileMapSlotRef.current}
								</div>
								<div className="mx-4 h-px bg-gray-200 md:hidden" />
							</>
						)}
						{children}
					</div>
				);
			},
		[],
	);

	const rerender = useDebouncedCallback(() => setRenderKey((k) => k + 1), {
		leading: false,
		trailing: true,
	});

	useEffect(() => {
		window.addEventListener("resize", rerender);
		window.addEventListener("scroll", rerender, true);

		return () => {
			window.removeEventListener("resize", rerender);
			window.removeEventListener("scroll", rerender, true);
		};
	}, [rerender]);

	useEffect(() => {
		const trigger = triggerRef.current;
		if (!trigger) return;

		const emitOpenState = () => {
			const isOpen = trigger.getAttribute("aria-expanded") === "true";
			isDropdownOpenRef.current = isOpen;
			setRenderKey((k) => k + 1);
			onDropdownOpenChangeRef.current?.(isOpen);
		};

		emitOpenState();
		const observer = new MutationObserver(emitOpenState);
		observer.observe(trigger, {
			attributes: true,
			attributeFilter: ["aria-expanded"],
		});

		return () => {
			observer.disconnect();
			isDropdownOpenRef.current = false;
			setRenderKey((k) => k + 1);
			onDropdownOpenChangeRef.current?.(false);
		};
	}, []);

	return (
		<SelectVendor
			{...props}
			triggerRef={triggerRef}
			contentWrapper={WithHeight as SelectVendorProps["contentWrapper"]}
		/>
	);
}
